import { Close as CloseIcon } from '@mui/icons-material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import {
    Box,
    Button,
    ButtonGroup,
    buttonGroupClasses,
    IconButton,
    Paper,
    Popper,
    ToggleButton,
    Tooltip,
} from '@mui/material';
import { ReactElement, useEffect, useRef, useState } from 'react';

type SplitToggleButtonProps = {
    isSelected: boolean;
    value: string;
    onToggle: () => void;
    icon: ReactElement;
    tooltipTitle: string;
    popperComponent: ReactElement;
    disabled?: boolean;
};

const SplitToggleButton = ({
    isSelected,
    value,
    onToggle,
    icon,
    tooltipTitle,
    popperComponent,
    disabled = false,
}: SplitToggleButtonProps) => {
    const [open, setOpen] = useState(false);
    const [mouseOver, setMouseOver] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isSelected) {
            setOpen(false);
        }
    }, [isSelected]);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
        onToggle();
    };

    return (
        <Box
            position="relative"
            onMouseEnter={() => {
                setMouseOver(true);
                setOpen(isSelected);
            }}
            onMouseLeave={() => {
                setMouseOver(false);
                setTimeout(() => {
                    if (!mouseOver) {
                        setOpen(false);
                    }
                }, 300);
            }}
        >
            <ButtonGroup
                variant="outlined"
                ref={anchorRef}
                size="small"
                disabled={disabled}
                sx={{
                    [`&.${buttonGroupClasses.root}`]: {
                        borderRadius: 0,
                    },
                    '& .MuiButtonGroup-grouped': {
                        minWidth: '0px',
                        padding: '0 2px',
                    },
                }}
            >
                <ToggleButton
                    value={value}
                    selected={isSelected}
                    onClick={onToggle}
                    disabled={disabled}
                    sx={(theme) => ({
                        lineHeight: 0,
                        borderRight: 0,
                        borderRadius: `${theme.shape.borderRadius}px 0 0 ${theme.shape.borderRadius}px !important`,
                    })}
                >
                    <Tooltip title={tooltipTitle}>{icon}</Tooltip>
                </ToggleButton>
                <Button
                    sx={(theme) => ({
                        borderColor: theme.palette.divider,
                        ':hover': {
                            borderColor: theme.palette.divider,
                        },
                    })}
                    onClick={handleToggle}
                    disabled={disabled}
                >
                    <ArrowDropDownIcon
                        sx={{ fontSize: 16 }}
                        color={disabled ? 'disabled' : 'action'}
                    />
                </Button>
            </ButtonGroup>
            <Popper
                sx={{
                    width: '400px',
                    zIndex: 1000,
                }}
                open={open && !disabled}
                anchorEl={anchorRef.current}
            >
                <Paper
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        mt: 0.5,
                    }}
                    onMouseLeave={() => {
                        setTimeout(() => {
                            setOpen(false);
                        }, 300);
                    }}
                >
                    <IconButton
                        onClick={() => setOpen(false)}
                        sx={{
                            position: 'absolute',
                            zIndex: 2000,
                            right: 8,
                            top: 8,
                        }}
                        disabled={disabled}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                    {popperComponent}
                </Paper>
            </Popper>
        </Box>
    );
};

export default SplitToggleButton;
